import React, { useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, Typography, Grid, Box, Chip, Paper } from '@mui/material';
import { useAtomValue } from 'jotai';
import { allSitesFiberSitePlanning } from '../../common/atom/atoms';
import { useFiberDashboard } from './context/fiberDashboardContext';
import FancyLoader from '../../common/components/FancyLoader.component';
import * as echarts from 'echarts';

export default function FiberSitePlanningOverview() {
  const data = useAtomValue(allSitesFiberSitePlanning);
  const validData = Array?.isArray(data) ? data : [];
  const { dashboardData, loading, error, isInitialized, fetchDashboardData } = useFiberDashboard();

  console.log('cjk', validData);
  console.log('Dashboard data from context:', dashboardData);

  // Ensure dashboard data is loaded when overview component mounts
  useEffect(() => {
    if (!isInitialized && !loading) {
      console.log('Overview component mounted - ensuring dashboard data is loaded');
      fetchDashboardData();
    }
  }, []);

  // Chart refs
  const monthlyChartRef = useRef(null);
  const yearlyChartRef = useRef(null);
  const statusChartRef = useRef(null);
  const regionalChartRef = useRef(null);
  const vendorChartRef = useRef(null);
  const progressChartRef = useRef(null);
  const interactiveChartRef = useRef(null);

  // Mini chart refs for stats cards
  const totalSitesChartRef = useRef(null);
  const onAirChartRef = useRef(null);
  const inProgressChartRef = useRef(null);
  const plannedChartRef = useRef(null);
  const nonFiberChartRef = useRef(null);
  const descopedChartRef = useRef(null);

  // Calculate statistics using dashboard data
  const totalSites = dashboardData.length;

  // Helper function to get status from different possible field names
  const getStatus = (site) => {
    return site.status || site.site_status || site.fiber_status || site.current_status;
  };

  // Count sites by status, handling null values
  const onAirSites = dashboardData.filter((site) => getStatus(site) === 'On-Air').length;
  const inProgressSites = dashboardData.filter((site) => getStatus(site) === 'In-Progress').length;
  const plannedSites = dashboardData.filter((site) => getStatus(site) === 'Planned').length;
  const nonFiberSites = dashboardData.filter((site) => getStatus(site) === 'Non-Fiber').length;
  const descopedSites = dashboardData.filter((site) => getStatus(site) === 'Descoped').length;
  const nullStatusSites = dashboardData.filter(
    (site) => getStatus(site) === null || getStatus(site) === undefined || getStatus(site) === ''
  ).length;

  // Calculate total length (keep using validData for detailed fields like total_length)
  const totalLength = validData.reduce((sum, site) => {
    const length = parseFloat(site.total_length) || 0;
    return sum + length;
  }, 0);

  // Debug: Log the actual counts
  console.log('Site counts:', {
    total: totalSites,
    onAir: onAirSites,
    inProgress: inProgressSites,
    planned: plannedSites,
    nonFiber: nonFiberSites,
    descoped: descopedSites,
    nullStatus: nullStatusSites,
  });

  console.log('validData', validData);

  // Debug: Check unique status values
  const uniqueStatuses = [...new Set(validData.map((site) => site.status).filter(Boolean))];
  console.log('Unique statuses in data:', uniqueStatuses);

  // Debug: Check field names
  if (validData.length > 0) {
    console.log('Available fields in data:', Object.keys(validData[0]));
    console.log('Sample site data:', validData[0]);
  }

  // Calculate regional breakdown using dashboard data
  const regionalData = useMemo(() => {
    const regions = {};

    // Debug: Log all region field values for first few sites
    console.log('=== REGIONAL DATA DEBUG ===');
    dashboardData.slice(0, 5).forEach((site, index) => {
      console.log(`Site ${index + 1} region fields:`, {
        region: site.region,
        pta_province: site.pta_province,
        province: site.province,
        area: site.area,
        pta_district: site.pta_district,
      });
    });

    dashboardData.forEach((site) => {
      // Try different possible field names for region
      let region =
        site.region ||
        site.pta_province ||
        site.province ||
        site.area ||
        site.pta_district ||
        'Unknown';

      // Clean the region name to prevent duplicates
      if (region && region !== 'Unknown') {
        region = region.trim(); // Remove leading/trailing spaces
        region = region.replace(/\s+/g, ' '); // Replace multiple spaces with single space
      }

      // Debug: Log which field was used for each site
      console.log(`Site ${site.site_code || site.id}: Using region="${region}" from fields:`, {
        region: site.region,
        pta_province: site.pta_province,
        province: site.province,
        area: site.area,
        pta_district: site.pta_district,
      });

      if (!regions[region]) {
        regions[region] = { onAir: 0, inProgress: 0, planned: 0, nonFiber: 0, descoped: 0 };
      }

      // Use the exact same logic as the main statistics
      const status = getStatus(site);
      if (status) {
        // Use exact string matching like in the main statistics
        if (status === 'On-Air') {
          regions[region].onAir += 1;
        } else if (status === 'In-Progress') {
          regions[region].inProgress += 1;
        } else if (status === 'Planned') {
          regions[region].planned += 1;
        } else if (status === 'Non-Fiber') {
          regions[region].nonFiber += 1;
        } else if (status === 'Descoped') {
          regions[region].descoped += 1;
        }

        console.log(`Processing: Region="${region}", Status="${status}", Counted as: ${status}`);
      }
    });

    console.log('Regional breakdown data:', regions);

    // Check for potential duplicates or similar names
    const regionNames = Object.keys(regions);
    console.log('All region names found:', regionNames);

    // Check for similar names that might be duplicates
    const similarRegions = [];
    for (let i = 0; i < regionNames.length; i++) {
      for (let j = i + 1; j < regionNames.length; j++) {
        const name1 = regionNames[i].toLowerCase();
        const name2 = regionNames[j].toLowerCase();
        if (name1.includes(name2) || name2.includes(name1)) {
          similarRegions.push([regionNames[i], regionNames[j]]);
        }
      }
    }

    if (similarRegions.length > 0) {
      console.warn('Potential duplicate/similar region names found:', similarRegions);
    }

    // Final deduplication: Check for exact duplicates and merge them
    const finalRegions = {};
    Object.entries(regions).forEach(([regionName, counts]) => {
      const cleanName = regionName.trim();
      if (finalRegions[cleanName]) {
        // Merge counts if duplicate found
        console.warn(`Merging duplicate region: "${regionName}" into "${cleanName}"`);
        finalRegions[cleanName].onAir += counts.onAir;
        finalRegions[cleanName].inProgress += counts.inProgress;
        finalRegions[cleanName].planned += counts.planned;
        finalRegions[cleanName].nonFiber += counts.nonFiber;
        finalRegions[cleanName].descoped += counts.descoped;
      } else {
        finalRegions[cleanName] = { ...counts };
      }
    });

    console.log('Final deduplicated regions:', finalRegions);
    console.log(
      'Regional data for chart:',
      Object.entries(finalRegions).map(([region, counts]) => ({
        region,
        onAir: counts.onAir,
        inProgress: counts.inProgress,
        planned: counts.planned,
        nonFiber: counts.nonFiber,
        descoped: counts.descoped,
      }))
    );

    return Object.entries(finalRegions).map(([region, counts]) => ({
      region,
      onAir: counts.onAir,
      inProgress: counts.inProgress,
      planned: counts.planned,
      nonFiber: counts.nonFiber,
      descoped: counts.descoped,
    }));
  }, [dashboardData]);

  // Calculate vendor breakdown using dashboard data
  const vendorData = useMemo(() => {
    const vendors = {};
    dashboardData.forEach((site) => {
      // Try different possible field names for vendor
      const vendor =
        site.vendor || site.vendor_name || site.contractor || site.partner || 'Unknown';
      vendors[vendor] = (vendors[vendor] || 0) + 1;
    });

    console.log('Vendor breakdown data:', vendors);

    return Object.entries(vendors).map(([vendor, count]) => ({
      vendor,
      count,
    }));
  }, [dashboardData]);

  // Calculate monthly trends (mock data for now)
  const monthlyData = [
    { month: 'Jan', onAir: 201, inProgress: 150 },
    { month: 'Feb', onAir: 126, inProgress: 180 },
    { month: 'Mar', onAir: 213, inProgress: 200 },
    { month: 'Apr', onAir: 214, inProgress: 220 },
    { month: 'May', onAir: 250, inProgress: 240 },
    { month: 'Jun', onAir: 352, inProgress: 260 },
    { month: 'Jul', onAir: 289, inProgress: 280 },
    { month: 'Aug', onAir: 247, inProgress: 300 },
    { month: 'Sep', onAir: 314, inProgress: 320 },
    { month: 'Oct', onAir: 204, inProgress: 340 },
    { month: 'Nov', onAir: 216, inProgress: 360 },
    { month: 'Dec', onAir: 259, inProgress: 380 },
  ];

  // Calculate yearly trends
  const yearlyData = [
    { year: '2020', onAir: 266 },
    { year: '2021', onAir: 474 },
    { year: '2022', onAir: 338 },
    { year: '2023', onAir: 766 },
    { year: '2024', onAir: 798 },
    { year: '2025', onAir: 243 },
  ];

  // Status distribution for pie chart
  const statusData = [
    { name: 'On-Air', value: onAirSites, color: '#12A150' },
    { name: 'In-Progress', value: inProgressSites, color: '#2196F3' },
    { name: 'Planned', value: plannedSites, color: '#BD1D23' },
    { name: 'Non-Fiber', value: nonFiberSites, color: '#FF9800' },
    { name: 'Descoped', value: descopedSites, color: '#9C27B0' },
    { name: 'No Status', value: nullStatusSites, color: '#9E9E9E' },
  ];

  const stats = [
    {
      title: 'Total Sites',
      value: totalSites,
      color: '#2196F3',
      bgColor: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
      icon: '📊',
      chartRef: totalSitesChartRef,
      chartType: 'area',
      chartData: monthlyData.map((item) => item.onAir + item.inProgress),
      trend: totalSites > 0 ? `+${Math.round((totalSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'On Air',
      value: onAirSites,
      color: '#12A150',
      bgColor: 'linear-gradient(135deg, #E8FAF0 0%, #C8E6C9 100%)',
      icon: '✅',
      chartRef: onAirChartRef,
      chartType: 'gauge',
      percentage: totalSites > 0 ? Math.round((onAirSites / totalSites) * 100) : 0,
      trend: totalSites > 0 ? `+${Math.round((onAirSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'In Progress',
      value: inProgressSites,
      color: '#2196F3',
      bgColor: 'linear-gradient(135deg, #E5F6FD 0%, #B3E5FC 100%)',
      icon: '⏳',
      chartRef: inProgressChartRef,
      chartType: 'line',
      chartData: monthlyData.map((item) => item.inProgress),
      trend: totalSites > 0 ? `+${Math.round((inProgressSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'Planned',
      value: plannedSites,
      color: '#BD1D23',
      bgColor: 'linear-gradient(135deg, #F8E8E9 0%, #FFCDD2 100%)',
      icon: '📋',
      chartRef: plannedChartRef,
      chartType: 'bar',
      chartData: [plannedSites, totalSites - plannedSites],
      trend: totalSites > 0 ? `+${Math.round((plannedSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'Non Fiber',
      value: nonFiberSites,
      color: '#FF9800',
      bgColor: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
      icon: '🚫',
      chartRef: nonFiberChartRef,
      chartType: 'donut',
      percentage: totalSites > 0 ? Math.round((nonFiberSites / totalSites) * 100) : 0,
      trend: totalSites > 0 ? `+${Math.round((nonFiberSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'Descoped',
      value: descopedSites,
      color: '#9C27B0',
      bgColor: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
      icon: '❌',
      chartRef: descopedChartRef,
      chartType: 'wave',
      percentage: totalSites > 0 ? Math.round((descopedSites / totalSites) * 100) : 0,
      trend: totalSites > 0 ? `+${Math.round((descopedSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'No Status',
      value: nullStatusSites,
      color: '#9E9E9E',
      bgColor: 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
      icon: '❓',
      chartRef: null,
      chartType: null,
      percentage: totalSites > 0 ? Math.round((nullStatusSites / totalSites) * 100) : 0,
      trend: totalSites > 0 ? `+${Math.round((nullStatusSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#9E9E9E',
    },
    {
      title: 'Total Length',
      value: totalLength.toFixed(2),
      color: '#FF5722',
      bgColor: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
      icon: '📏',
      chartRef: null, // No mini chart for this stat
      chartType: null,
      trend: `${totalLength > 0 ? '+' : ''}${totalLength.toFixed(2)} km`,
      trendColor: '#FF5722',
    },
    {
      title: 'Vendors',
      value: vendorData.length,
      color: '#673AB7',
      bgColor: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
      icon: '🏢',
      chartRef: null, // No mini chart for this stat
      chartType: null,
      trend: `+${vendorData.length} vendors`,
      trendColor: '#673AB7',
    },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Progress bar symbols
  const progressSymbols = {
    onAir:
      'path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    inProgress:
      'path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
    planned:
      'path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
    nonFiber:
      'path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z',
    descoped:
      'path://M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  };

  // Initialize ECharts
  useEffect(() => {
    const charts = [];

    // Monthly Trends Chart
    if (monthlyChartRef.current) {
      const monthlyChart = echarts.init(monthlyChartRef.current);
      const monthlyOption = {
        title: { text: 'Monthly Site Trends', left: 'center' },
        tooltip: { trigger: 'axis' },
        legend: { data: ['On-Air', 'In-Progress'] },
        xAxis: { type: 'category', data: monthlyData.map((item) => item.month) },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'On-Air',
            type: 'bar',
            data: monthlyData.map((item) => item.onAir),
            itemStyle: { color: '#12A150' },
          },
          {
            name: 'In-Progress',
            type: 'bar',
            data: monthlyData.map((item) => item.inProgress),
            itemStyle: { color: '#2196F3' },
          },
        ],
      };
      monthlyChart.setOption(monthlyOption);
      charts.push(monthlyChart);
    }

    // Yearly Trends Chart
    if (yearlyChartRef.current) {
      const yearlyChart = echarts.init(yearlyChartRef.current);
      const yearlyOption = {
        title: { text: 'Yearly On-Air Sites', left: 'center' },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: yearlyData.map((item) => item.year) },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'On-Air',
            type: 'line',
            data: yearlyData.map((item) => item.onAir),
            itemStyle: { color: '#12A150' },
            lineStyle: { color: '#12A150', width: 3 },
          },
        ],
      };
      yearlyChart.setOption(yearlyOption);
      charts.push(yearlyChart);
    }

    // Status Distribution Chart (Donut Chart)
    if (statusChartRef.current) {
      const statusChart = echarts.init(statusChartRef.current);
      const statusOption = {
        // title: {
        //   show: false, // Hide the title
        // },
        title: { text: 'Status Distribution', left: 'center', bottom: 10 },
        tooltip: {
          trigger: 'item',
        },
        legend: {
          top: '5%',
          left: 'center',
        },
        series: [
          {
            name: 'Status Distribution',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2,
            },
            label: {
              show: false,
              position: 'center',
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 40,
                fontWeight: 'bold',
              },
            },
            labelLine: {
              show: false,
            },
            data: statusData,
          },
        ],
      };
      statusChart.setOption(statusOption);
      charts.push(statusChart);
    }

    // Regional Breakdown Chart
    if (regionalChartRef.current) {
      const regionalChart = echarts.init(regionalChartRef.current);

      console.log('Regional chart data being used:', regionalData);
      console.log(
        'Regions:',
        regionalData.map((item) => item.region)
      );
      console.log(
        'On-Air data:',
        regionalData.map((item) => item.onAir)
      );
      console.log(
        'In-Progress data:',
        regionalData.map((item) => item.inProgress)
      );

      const regionalOption = {
        title: { text: 'Regional Site Distribution', left: 'center' },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['On-Air', 'In-Progress', 'Planned', 'Non-Fiber', 'Descoped'] },
        xAxis: { type: 'category', data: regionalData.map((item) => item.region) },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'On-Air',
            type: 'bar',
            stack: 'total',
            data: regionalData.map((item) => item.onAir),
            itemStyle: { color: '#12A150' },
          },
          {
            name: 'In-Progress',
            type: 'bar',
            stack: 'total',
            data: regionalData.map((item) => item.inProgress),
            itemStyle: { color: '#2196F3' },
          },
          {
            name: 'Planned',
            type: 'bar',
            stack: 'total',
            data: regionalData.map((item) => item.planned),
            itemStyle: { color: '#BD1D23' },
          },
          {
            name: 'Non-Fiber',
            type: 'bar',
            stack: 'total',
            data: regionalData.map((item) => item.nonFiber),
            itemStyle: { color: '#FF9800' },
          },
          {
            name: 'Descoped',
            type: 'bar',
            stack: 'total',
            data: regionalData.map((item) => item.descoped),
            itemStyle: { color: '#9C27B0' },
          },
        ],
      };
      regionalChart.setOption(regionalOption);
      charts.push(regionalChart);
    }

    // Vendor Scope Chart (Vendors and their site counts)
    if (vendorChartRef.current) {
      const vendorChart = echarts.init(vendorChartRef.current);

      // Calculate vendor site counts
      const vendorCounts = {};
      validData.forEach((site) => {
        const vendor =
          site.vendor || site.vendor_name || site.contractor || site.partner || 'Unknown';
        vendorCounts[vendor] = (vendorCounts[vendor] || 0) + 1;
      });

      // Sort vendors by site count (descending)
      const sortedVendors = Object.entries(vendorCounts)
        .sort(([, a], [, b]) => b - a)
        .map(([vendor, count]) => ({ vendor, count }));

      const vendorOption = {
        title: {
          text: 'Vendor Scope',
          left: 'center',
          textStyle: { color: '#333' },
        },
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} sites ({d}%)',
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: 'middle',
          textStyle: { color: '#333' },
        },
        series: [
          {
            name: 'Vendor Sites',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['60%', '50%'],
            data: sortedVendors.map((item, index) => ({
              name: item.vendor,
              value: item.count,
              itemStyle: {
                color: COLORS[index % COLORS.length],
              },
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
            label: {
              show: true,
              formatter: '{b}\n{c} sites',
              fontSize: 12,
              fontWeight: 'bold',
            },
            labelLine: {
              show: true,
            },
          },
        ],
      };
      vendorChart.setOption(vendorOption);
      charts.push(vendorChart);
    }

    // Progress Overview Chart (Pictorial Bar)
    if (progressChartRef.current) {
      const progressChart = echarts.init(progressChartRef.current);

      const progressData = [
        { name: 'On-Air', value: onAirSites, symbol: progressSymbols.onAir, color: '#12A150' },
        {
          name: 'In-Progress',
          value: inProgressSites,
          symbol: progressSymbols.inProgress,
          color: '#2196F3',
        },
        { name: 'Planned', value: plannedSites, symbol: progressSymbols.planned, color: '#BD1D23' },
        {
          name: 'Non-Fiber',
          value: nonFiberSites,
          symbol: progressSymbols.nonFiber,
          color: '#FF9800',
        },
        {
          name: 'Descoped',
          value: descopedSites,
          symbol: progressSymbols.descoped,
          color: '#9C27B0',
        },
        {
          name: 'No Status',
          value: nullStatusSites,
          symbol: '❓',
          color: '#9E9E9E',
        },
      ];

      const progressOption = {
        title: { text: 'Progress Overview', left: 'center' },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: function (params) {
            const data = params[0];
            const percentage = totalSites > 0 ? ((data.value / totalSites) * 100).toFixed(1) : 0;
            return `${data.name}<br/>Count: ${data.value}<br/>Percentage: ${percentage}%`;
          },
        },
        grid: {
          containLabel: true,
          left: 20,
          right: 80, // Increased right margin to prevent cutting off
          top: 40,
          bottom: 20,
        },
        yAxis: {
          data: progressData.map((item) => item.name),
          inverse: true,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            margin: 35, // Increased margin for better spacing
            fontSize: 13, // Slightly smaller font to fit better
            color: '#333',
            width: 80, // Fixed width for consistent spacing
            overflow: 'truncate', // Handle text overflow
          },
        },
        xAxis: {
          splitLine: { show: false },
          axisLabel: { show: false },
          axisTick: { show: false },
          axisLine: { show: false },
        },
        series: [
          {
            name: 'Progress',
            type: 'pictorialBar',
            label: {
              show: true,
              position: 'right',
              offset: [15, 0], // Increased offset to prevent overlap
              fontSize: 12, // Smaller font size for better fit
              formatter: function (params) {
                const percentage =
                  totalSites > 0 ? ((params.value / totalSites) * 100).toFixed(1) : 0;
                return `${params.value} (${percentage}%)`;
              },
            },
            symbolRepeat: true,
            symbolSize: ['70%', '50%'], // Reduced symbol size for better spacing
            barCategoryGap: '50%', // Increased gap between categories
            data: progressData.map((item) => ({
              value: item.value,
              symbol: item.symbol,
              itemStyle: { color: item.color },
            })),
          },
        ],
      };
      progressChart.setOption(progressOption);
      charts.push(progressChart);
    }

    // Mini Charts for Stats Cards
    stats.forEach((stat, index) => {
      if (stat.chartRef && stat.chartRef.current) {
        const miniChart = echarts.init(stat.chartRef.current);
        let miniOption = {};

        switch (stat.chartType) {
          case 'area':
            miniOption = {
              grid: { left: 0, right: 0, top: 0, bottom: 0 },
              xAxis: {
                type: 'category',
                show: false,
                data: Array.from({ length: 12 }, (_, i) => i),
              },
              yAxis: { type: 'value', show: false },
              series: [
                {
                  type: 'line',
                  data: stat.chartData || [],
                  smooth: true,
                  symbol: 'none',
                  lineStyle: { color: stat.color, width: 2 },
                  areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                      { offset: 0, color: stat.color + '40' },
                      { offset: 1, color: stat.color + '10' },
                    ]),
                  },
                },
              ],
            };
            break;

          case 'gauge':
            miniOption = {
              series: [
                {
                  type: 'gauge',
                  startAngle: 180,
                  endAngle: 0,
                  center: ['50%', '75%'],
                  radius: '90%',
                  min: 0,
                  max: 100,
                  splitNumber: 8,
                  axisLine: {
                    lineStyle: {
                      width: 6,
                      color: [
                        [stat.percentage / 100, stat.color],
                        [1, '#E0E0E0'],
                      ],
                    },
                  },
                  pointer: { show: false },
                  axisTick: { show: false },
                  splitLine: { show: false },
                  axisLabel: { show: false },
                  detail: { show: false },
                  data: [{ value: stat.percentage }],
                },
              ],
            };
            break;

          case 'line':
            miniOption = {
              grid: { left: 0, right: 0, top: 5, bottom: 5 },
              xAxis: {
                type: 'category',
                show: false,
                data: Array.from({ length: 12 }, (_, i) => i),
              },
              yAxis: { type: 'value', show: false },
              series: [
                {
                  type: 'line',
                  data: stat.chartData || [],
                  smooth: true,
                  symbol: 'circle',
                  symbolSize: 4,
                  lineStyle: { color: stat.color, width: 3 },
                  itemStyle: { color: stat.color },
                },
              ],
            };
            break;

          case 'bar':
            miniOption = {
              grid: { left: 5, right: 5, top: 5, bottom: 5 },
              xAxis: { type: 'category', show: false, data: ['Current', 'Remaining'] },
              yAxis: { type: 'value', show: false },
              series: [
                {
                  type: 'bar',
                  data: stat.chartData || [],
                  itemStyle: {
                    color: function (params) {
                      return params.dataIndex === 0 ? stat.color : '#E0E0E0';
                    },
                  },
                  barWidth: '60%',
                },
              ],
            };
            break;

          case 'donut':
            miniOption = {
              series: [
                {
                  type: 'pie',
                  radius: ['50%', '80%'],
                  center: ['50%', '50%'],
                  data: [
                    { value: stat.percentage, itemStyle: { color: stat.color } },
                    { value: 100 - stat.percentage, itemStyle: { color: '#E0E0E0' } },
                  ],
                  label: { show: false },
                  labelLine: { show: false },
                },
              ],
            };
            break;

          case 'wave': {
            const waveData = Array.from(
              { length: 20 },
              (_, i) => Math.sin(i * 0.3) * 30 + 50 + Math.random() * 10
            );
            miniOption = {
              grid: { left: 0, right: 0, top: 0, bottom: 0 },
              xAxis: {
                type: 'category',
                show: false,
                data: Array.from({ length: 20 }, (_, i) => i),
              },
              yAxis: { type: 'value', show: false },
              series: [
                {
                  type: 'line',
                  data: waveData,
                  smooth: true,
                  symbol: 'none',
                  lineStyle: { color: stat.color, width: 2 },
                  areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                      { offset: 0, color: stat.color + '30' },
                      { offset: 1, color: stat.color + '05' },
                    ]),
                  },
                },
              ],
            };
            break;
          }
        }

        miniChart.setOption(miniOption);
        charts.push(miniChart);
      }
    });

    // Interactive Chart Example with Real Data
    if (interactiveChartRef.current) {
      // Check if data is available, if not, wait and retry
      if (!validData || validData.length === 0) {
        console.log('Data not ready, waiting...');
        interactiveChartRef.current.innerHTML = `
          <div style="
            display: flex; 
            align-items: center; 
            justify-content: center; 
            height: 100%; 
            color: #333; 
            font-size: 16px;
            text-align: center;
            padding: 20px;
            background-color: #d1ecf1;
            border: 2px solid #17a2b8;
          ">
            <div>
              <h3>Loading Data...</h3>
              <p>Please wait for data to load</p>
              <p>Current data length: ${validData?.length || 0}</p>
            </div>
          </div>
        `;

        // Retry after a short delay
        setTimeout(() => {
          if (interactiveChartRef.current && validData && validData.length > 0) {
            console.log('Retrying chart initialization...');
            // Re-run the chart initialization
            const event = new Event('resize');
            window.dispatchEvent(event);
          }
        }, 1000);

        return;
      }

      const interactiveChart = echarts.init(interactiveChartRef.current);

      // Debug: Log the raw data
      console.log('Raw validData:', validData);
      console.log('Sample site object:', validData[0]);
      console.log('Total sites:', validData.length);
      console.log('Is validData array?', Array.isArray(validData));
      console.log('validData type:', typeof validData);

      // Check if data is loaded
      if (!validData || validData.length === 0) {
        console.warn('validData is empty or undefined');
        interactiveChartRef.current.innerHTML = `
          <div style="
            display: flex; 
            align-items: center; 
            justify-content: center; 
            height: 100%; 
            color: #333; 
            font-size: 16px;
            text-align: center;
            padding: 20px;
            background-color: #f8d7da;
            border: 2px solid #dc3545;
          ">
            <div>
              <h3>Data Not Loaded Yet</h3>
              <p>validData is empty or undefined</p>
              <p>Length: ${validData?.length || 'undefined'}</p>
              <p>Type: ${typeof validData}</p>
              <p>Please wait for data to load...</p>
            </div>
          </div>
        `;
        return;
      }

      // Get unique vendors and regions from API data
      const vendors = [
        ...new Set(
          validData.map(
            (site) =>
              site.vendor || site.vendor_name || site.contractor || site.partner || 'Unknown'
          )
        ),
      ].filter((vendor) => vendor !== 'Unknown');

      const regions = [
        ...new Set(
          validData.map(
            (site) =>
              site.pta_province || site.region || site.province || site.pta_district || 'Unknown'
          )
        ),
      ].filter((region) => region !== 'Unknown');

      // Additional debugging for vendor extraction
      console.log(
        'All vendor values found:',
        validData.map((site) => site.vendor)
      );
      console.log(
        'All region values found:',
        validData.map((site) => site.pta_province)
      );
      console.log(
        'Raw vendor field values:',
        validData.map((site) => ({
          vendor: site.vendor,
          vendor_name: site.vendor_name,
          contractor: site.contractor,
          partner: site.partner,
        }))
      );

      console.log('Extracted vendors:', vendors);
      console.log('Extracted regions:', regions);

      // If no data, show a simple message
      if (vendors.length === 0 || regions.length === 0) {
        console.warn('No vendors or regions found in data');
        interactiveChartRef.current.innerHTML = `
          <div style="
            display: flex; 
            align-items: center; 
            justify-content: center; 
            height: 100%; 
            color: #333; 
            font-size: 16px;
            text-align: center;
            padding: 20px;
            background-color: #f0f0f0;
            border: 2px solid #ccc;
          ">
            <div>
              <h3>No Data Available</h3>
              <p>Vendors found: ${vendors.length}</p>
              <p>Regions found: ${regions.length}</p>
              <p>Total sites: ${validData.length}</p>
              <p>Sample vendor: ${validData[0]?.vendor || 'N/A'}</p>
              <p>Sample region: ${validData[0]?.pta_province || 'N/A'}</p>
            </div>
          </div>
        `;
        return;
      }

      // Create dataset with vendors as columns and regions as rows
      const chartData = [['region', ...vendors]];

      regions.forEach((region) => {
        const row = [region];
        vendors.forEach((vendor) => {
          const count = validData.filter((site) => {
            const siteRegion =
              site.pta_province || site.region || site.province || site.pta_district || 'Unknown';
            const siteVendor =
              site.vendor || site.vendor_name || site.contractor || site.partner || 'Unknown';
            return siteRegion === region && siteVendor === vendor;
          }).length;
          row.push(count);
        });
        chartData.push(row);
      });

      console.log('Interactive chart data:', chartData);

      // Check if we have any non-zero data
      const hasData = chartData.slice(1).some((row) => row.slice(1).some((count) => count > 0));

      if (!hasData) {
        console.warn('No site data found for vendor-region combinations');
        console.log('Chart data breakdown:', chartData);

        // Show a simple test chart instead of error
        console.log('Showing simple test chart...');
        const testOption = {
          title: {
            text: 'Test Chart - ECharts Working',
            left: 'center',
            top: 20,
            textStyle: { color: '#333', fontSize: 16 },
          },
          xAxis: {
            type: 'category',
            data: ['Test1', 'Test2', 'Test3'],
            axisLabel: { color: '#333' },
          },
          yAxis: {
            type: 'value',
            axisLabel: { color: '#333' },
          },
          series: [
            {
              type: 'bar',
              data: [10, 20, 30],
              itemStyle: { color: '#4CAF50' },
            },
          ],
        };

        try {
          interactiveChart.setOption(testOption);
          console.log('Test chart loaded successfully');
          charts.push(interactiveChart);
          return;
        } catch (testError) {
          console.error('Test chart failed:', testError);
        }

        interactiveChartRef.current.innerHTML = `
          <div style="
            display: flex; 
            align-items: center; 
            justify-content: center; 
            height: 100%; 
            color: #333; 
            font-size: 16px;
            text-align: center;
            padding: 20px;
            background-color: #fff3cd;
            border: 2px solid #ffc107;
          ">
            <div>
              <h3>No Site Data Found</h3>
              <p>No sites found for the vendor-region combinations</p>
              <p>Chart data: ${JSON.stringify(chartData)}</p>
              <p>Try checking the data structure</p>
            </div>
          </div>
        `;
        return;
      }

      const option = {
        title: {
          text: 'Vendor Distribution by Region - Interactive View',
          left: 'center',
          top: 10,
          textStyle: { color: '#333', fontSize: 16 },
        },
        legend: {
          data: vendors,
          top: 40,
          textStyle: { color: '#333' },
        },
        tooltip: {
          trigger: 'axis',
          showContent: false,
        },
        dataset: {
          source: chartData,
        },
        xAxis: {
          type: 'category',
          axisLabel: {
            color: '#333',
            rotate: 45,
            fontSize: 11,
          },
        },
        yAxis: {
          gridIndex: 0,
          name: 'Site Count',
          nameTextStyle: { color: '#333' },
          axisLabel: { color: '#333' },
        },
        grid: {
          top: '55%',
          left: '10%',
          right: '10%',
          bottom: '15%',
        },
        series: [
          // Line charts for each region
          ...regions.map((_, index) => ({
            type: 'line',
            smooth: true,
            seriesLayoutBy: 'row',
            emphasis: { focus: 'series' },
            lineStyle: { width: 3 },
            symbolSize: 6,
          })),
          // Pie chart
          {
            type: 'pie',
            id: 'pie',
            radius: '25%',
            center: ['50%', '25%'],
            emphasis: {
              focus: 'self',
            },
            label: {
              formatter: '{b}: {c} sites ({d}%)',
              fontSize: 11,
              color: '#333',
            },
            encode: {
              itemName: 'region',
              value: vendors[0], // Start with first vendor
              tooltip: vendors[0],
            },
          },
        ],
      };

      // Add the updateAxisPointer event handler
      interactiveChart.on('updateAxisPointer', function (event) {
        const xAxisInfo = event.axesInfo[0];
        if (xAxisInfo && xAxisInfo.value < vendors.length) {
          const vendorIndex = xAxisInfo.value;
          const vendorName = vendors[vendorIndex];

          interactiveChart.setOption({
            series: {
              id: 'pie',
              label: {
                formatter: `{b}: {@[${vendorName}]} sites ({d}%)`,
              },
              encode: {
                value: vendorName,
                tooltip: vendorName,
              },
            },
          });
        }
      });

      try {
        interactiveChart.setOption(option);
        console.log('Interactive chart loaded successfully');
        charts.push(interactiveChart);
      } catch (error) {
        console.error('Error setting chart option:', error);
        // Fallback to a simple chart
        const fallbackOption = {
          title: {
            text: 'Vendor Distribution by Region (Simple View)',
            left: 'center',
            top: 20,
            textStyle: { color: '#333', fontSize: 16 },
          },
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} sites ({d}%)',
          },
          legend: {
            orient: 'vertical',
            left: 'left',
            top: 'middle',
            textStyle: { color: '#333' },
          },
          series: [
            {
              name: 'Vendor Sites',
              type: 'pie',
              radius: '50%',
              center: ['60%', '50%'],
              data: vendors.map((vendor, index) => {
                const totalSites = validData.filter((site) => {
                  const siteVendor =
                    site.vendor || site.vendor_name || site.contractor || site.partner || 'Unknown';
                  return siteVendor === vendor;
                }).length;
                return {
                  name: vendor,
                  value: totalSites,
                  itemStyle: {
                    color: COLORS[index % COLORS.length],
                  },
                };
              }),
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                },
              },
              label: {
                show: true,
                formatter: '{b}\n{c} sites',
                fontSize: 12,
                fontWeight: 'bold',
              },
            },
          ],
        };

        try {
          interactiveChart.setOption(fallbackOption);
          console.log('Fallback chart loaded successfully');
          charts.push(interactiveChart);
        } catch (fallbackError) {
          console.error('Even fallback chart failed:', fallbackError);
          interactiveChartRef.current.innerHTML = `
            <div style="
              display: flex; 
              align-items: center; 
              justify-content: center; 
              height: 100%; 
              color: #333; 
              font-size: 16px;
              text-align: center;
              padding: 20px;
            ">
              <div>
                <h3>Chart Failed to Load</h3>
                <p>Error: ${fallbackError.message}</p>
                <p>Please check console for details</p>
              </div>
            </div>
          `;
        }
      }
    }

    // Cleanup function
    return () => {
      charts.forEach((chart) => {
        if (chart && !chart.isDisposed()) {
          chart.dispose();
        }
      });
    };
  }, [
    monthlyData,
    yearlyData,
    statusData,
    regionalData,
    vendorData,
    onAirSites,
    inProgressSites,
    plannedSites,
    nonFiberSites,
    descopedSites,
    totalSites,
    validData, // Add dependency on validData
  ]);

  // Show error state if there's an error
  if (error && !loading) {
    return (
      <div
        style={{
          padding: '20px 2%',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#d32f2f',
              marginBottom: '16px',
              fontWeight: 600,
            }}
          >
            ⚠️ Something went wrong
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              marginBottom: '24px',
            }}
          >
            Unable to load dashboard data. Please try again later.
          </Typography>
          <button
            onClick={() => fetchDashboardData(true)}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Try Again
          </button>
        </Box>
      </div>
    );
  }

  // Show loading state while fetching dashboard data - but maintain layout structure
  if (loading || (!isInitialized && dashboardData.length === 0)) {
    return (
      <div
        style={{
          padding: '20px 2%',
          minHeight: '100vh',
          position: 'relative',
        }}
      >
        <FancyLoader
          message={loading ? 'Loading dashboard data...' : 'Initializing dashboard...'}
          size="large"
          fullScreen={true}
        />
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div
        style={{
          padding: '20px 2%',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            color: '#fff',
            '& .error-icon': {
              fontSize: '4rem',
              marginBottom: '1rem',
              animation: 'shake 0.5s ease-in-out infinite',
            },
          }}
        >
          <Typography variant="h2" className="error-icon">
            ⚠️
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 1 }}>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Error loading dashboard data. Please try again.
          </Typography>
        </Box>

        <style jsx>{`
          @keyframes shake {
            0%,
            100% {
              transform: translateX(0);
            }
            25% {
              transform: translateX(-5px);
            }
            75% {
              transform: translateX(5px);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '20px 2%',
        // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* Floating particles background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)
          `,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <Grid
        container
        spacing={2}
        sx={{
          mb: 4,
          position: 'relative',
          zIndex: 1,
          flexWrap: 'nowrap', // keep all cards in one row
          overflowX: 'auto', // allow horizontal scroll
          scrollbarWidth: 'thin', // Firefox
          '&::-webkit-scrollbar': {
            // Chrome/Edge
            height: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '4px',
          },
        }}
      >
        {stats.map((stat, index) => (
          <Grid item key={index} xs>
            {' '}
            {/* fixed width for each card */}
            <Card
              sx={{
                height: '180px',
                borderRadius: 4,
                background: stat.bgColor,
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0px 16px 48px rgba(0, 0, 0, 0.2)',
                  '&::before': {
                    height: '6px',
                  },
                  '& .stat-icon': {
                    transform: 'scale(1.2) rotate(5deg)',
                  },
                  '& .trend-chip': {
                    transform: 'scale(1.1)',
                  },
                },
                '@keyframes pulse': {
                  '0%': { boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)' },
                  '50%': { boxShadow: `0px 8px 32px ${stat.color}30` },
                  '100%': { boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)' },
                },
                animation: 'pulse 3s ease-in-out infinite',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: stat.color,
                  borderRadius: '4px 4px 0 0',
                },
              }}
            >
              <CardContent
                sx={{
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                {/* Header Section */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box
                    className="stat-icon"
                    sx={{
                      fontSize: '24px',
                      opacity: 0.8,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {stat.icon}
                  </Box>
                  {stat.trend && (
                    <Chip
                      className="trend-chip"
                      label={stat.trend}
                      size="small"
                      sx={{
                        backgroundColor: stat.trendColor + '20',
                        color: stat.trendColor,
                        fontWeight: 600,
                        fontSize: '10px',
                        height: '20px',
                        transition: 'all 0.3s ease',
                        '& .MuiChip-label': {
                          px: 1,
                        },
                      }}
                    />
                  )}
                </Box>

                {/* Value Section */}
                <Typography
                  variant="h3"
                  sx={{
                    color: stat.color,
                    fontWeight: 800,
                    fontSize: '28px',
                    lineHeight: 1,
                    mb: 0.5,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {stat.value.toLocaleString()}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 2,
                    opacity: 0.8,
                  }}
                >
                  {stat.title}
                </Typography>

                {/* Mini Chart Section */}
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                  <div
                    ref={stat.chartRef}
                    style={{
                      width: '100%',
                      height: '60px',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      paddingTop: '7px',
                      right: 0,
                    }}
                  />
                </Box>

                {/* Background Decoration */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${stat.color}15 0%, transparent 70%)`,
                    zIndex: 0,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
        {/* First Row: 3 graphs */}
        {/* Monthly Trends */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={monthlyChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Yearly Trends */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={yearlyChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Status Distribution */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent>
              <div ref={statusChartRef} style={{ width: '100%', height: '400px' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Second Row: 3 graphs */}
        {/* Regional Breakdown */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent>
              <div ref={regionalChartRef} style={{ width: '100%', height: '400px' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Vendor Scope */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent>
              <div ref={vendorChartRef} style={{ width: '100%', height: '400px' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Progress Overview */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent>
              <div ref={progressChartRef} style={{ width: '100%', height: '400px' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Third Row: 1 graph */}
        {/* Interactive Chart Example */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: '#333' }}>
                Interactive Vendor Distribution by Region
              </Typography>
              <div ref={interactiveChartRef} style={{ width: '100%', height: '500px' }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Stats */}
      <Box sx={{ mt: 4, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, color: '#000000', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
        >
          Quick Statistics
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#12A150', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {totalSites > 0 ? ((onAirSites / totalSites) * 100).toFixed(1) : 0}%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#12A150',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Completion Rate
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#2196F3', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {vendorData.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#2196F3',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Active Vendors
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#FF9800', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {regionalData.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#FF9800',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Regions Covered
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#FF5722', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {totalLength.toFixed(2)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#FF5722',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Total Length (km)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
